import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import tw, { type TwStyle } from 'twin.macro';
import getConsentCheckboxLabel from '@components/modules/RegisterForm/utils/getConsentCheckboxLabel';
import useTransformLinksToExternal from '@components/modules/RegisterForm/hooks/useTransformLinksToExternal';
import { type BrandTerm } from '@tsTypes/api/BrandTerm';
import type { Modules } from '@tsTypes/api/Modules';

export type PointsNames = Pick<
  Modules['MoneyBox'],
  'singularNameDenominator' | 'pluralNameDenominator' | 'pluralNameGenitive'
>;

const getPointsWord = (
  points: number,
  names: PointsNames | undefined,
  t: (key: string) => string
) => {
  const abs = Math.abs(points);

  if (abs === 1) {
    return names?.singularNameDenominator || t('$*common.point_1');
  }

  const lastDigit = abs % 10;
  const lastTwoDigits = abs % 100;

  if (
    lastDigit >= 2 &&
    lastDigit <= 4 &&
    !(lastTwoDigits >= 12 && lastTwoDigits <= 14)
  ) {
    return names?.pluralNameDenominator || t('$*common.point_2');
  }

  return names?.pluralNameGenitive || t('$*common.point_0');
};

type Props = {
  term: BrandTerm;
  bonus?: number | null;
  lineClamp?: number;
  pointsNames?: PointsNames;
  badgeCss?: TwStyle;
  expandCss?: TwStyle;
};

const ConsentCheckboxLabel = ({
  term,
  bonus = null,
  lineClamp,
  pointsNames,
  badgeCss,
  expandCss,
}: Props) => {
  const { t } = useTranslation();
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useTransformLinksToExternal([labelRef]);

  useEffect(() => {
    if (lineClamp && labelRef.current) {
      setIsOverflowed(
        labelRef.current.scrollHeight > labelRef.current.clientHeight
      );
    }
  }, [lineClamp]);

  return (
    <span tw="block">
      <span
        ref={labelRef}
        css={
          lineClamp &&
          !isExpanded && {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: lineClamp,
            overflow: 'hidden',
          }
        }
        dangerouslySetInnerHTML={{ __html: getConsentCheckboxLabel(term) }}
      />
      {lineClamp && isOverflowed && (
        <button
          type="button"
          css={[tw`font-semibold text-primary`, expandCss]}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setIsExpanded(prev => !prev);
          }}
        >
          {isExpanded ? t('$*common.collapse') : t('$*common.expand')}
        </button>
      )}
      {typeof bonus === 'number' && bonus > 0 && (
        <span
          data-testid="ConsentCheckbox__bonus"
          css={[
            tw`bg-primary mt-1 block w-fit whitespace-nowrap rounded px-2 py-0.5 text-sm font-semibold text-white`,
            badgeCss,
          ]}
        >
          {`+ ${bonus} ${getPointsWord(bonus, pointsNames, t)}`}
        </span>
      )}
    </span>
  );
};

export default ConsentCheckboxLabel;
